import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, TrendingDown, Minus, MessageCircle, Eye, Heart, Sparkles, Users } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

function Delta({ pct }) {
  const positive = pct > 0;
  const negative = pct < 0;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  const color = positive ? "text-emerald-600" : negative ? "text-[#E63946]" : "text-black/40";
  const sign = pct > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-widest ${color}`} data-testid="delta-pill">
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {sign}{pct}%
    </span>
  );
}

function fromNow(iso) {
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(1, Math.round((Date.now() - t) / 1000));
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
  } catch (e) { return ""; }
}

export default function Curator() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/curator/stats");
        setStats(data);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return null;
  if (loading) return <div className="max-w-6xl mx-auto px-6 py-16 text-xs font-mono uppercase tracking-widest text-black/40">Loading dashboard…</div>;
  if (!stats) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="curator-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]" data-testid="curator-back-link">
        <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
        Back to home
      </Link>

      <div className="mt-6 mb-10 border-b border-black/10 pb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" strokeWidth={2.5} />
            Curator dashboard
          </div>
          <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter">
            {user.name?.split(" ")[0] || "Curator"}&rsquo;s heat map
          </h1>
          <p className="mt-3 text-black/60 font-serif italic max-w-2xl">
            Live reaction counts, top emojis and week-over-week deltas from every one of your public wishlists.
          </p>
        </div>
        <Link to="/wishlists" className="inline-flex items-center gap-2 border-2 border-black text-black text-xs font-bold uppercase tracking-[0.25em] px-4 py-2.5 hover:bg-black hover:text-white" data-testid="curator-my-wishlists-link">
          My wishlists
        </Link>
      </div>

      {stats.wishlists_count === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center bg-white" data-testid="curator-empty">
          <div className="font-serif text-3xl mb-2">Nothing to measure yet</div>
          <p className="text-black/60 text-sm mb-6 max-w-md mx-auto">
            Publish your first wishlist and shoppers can leave reactions and comments — you&rsquo;ll see the heat map light up here.
          </p>
          <Link to="/bookmarks" className="inline-block bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-5 py-3 hover:bg-[#E63946]">
            Bundle bookmarks into a wishlist
          </Link>
        </div>
      ) : (
        <>
          {/* Headline stat grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { label: "Wishlists", value: stats.wishlists_count, icon: Sparkles, testid: "stat-wishlists" },
              { label: "Followers", value: stats.followers_count, icon: Users, testid: "stat-followers" },
              { label: "Total views", value: stats.views_total, icon: Eye, testid: "stat-views" },
              { label: "Reactions (7d)", value: stats.reactions.this_total, icon: Heart, testid: "stat-reactions", delta: stats.reactions.delta_pct },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="border-2 border-black bg-white p-5" data-testid={s.testid}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 inline-flex items-center gap-1">
                      <Icon className="w-3 h-3" strokeWidth={2} />
                      {s.label}
                    </div>
                    {s.delta !== undefined && <Delta pct={s.delta} />}
                  </div>
                  <div className="font-serif font-black text-3xl md:text-4xl tracking-tighter">{s.value}</div>
                </div>
              );
            })}
          </div>

          {/* Emoji breakdown */}
          <div className="mb-10">
            <div className="flex items-end justify-between border-b border-black/10 pb-3 mb-4 gap-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50">
                Emoji heat map &middot; this week vs last
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-black/40 inline-flex items-center gap-1">
                <MessageCircle className="w-3 h-3" strokeWidth={2} />
                {stats.comments.this_week} comments
                <span className="ml-2"><Delta pct={stats.comments.delta_pct} /></span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="emoji-breakdown">
              {stats.reaction_emojis.map((e) => {
                const t = stats.reactions.this_week[e] || 0;
                const l = stats.reactions.last_week[e] || 0;
                const diff = t - l;
                return (
                  <div key={e} className="border border-black/10 bg-white p-4 text-center" data-testid={`emoji-cell-${e}`}>
                    <div className="text-3xl leading-none">{e}</div>
                    <div className="mt-3 font-serif font-black text-2xl tracking-tighter">{t}</div>
                    <div className={`mt-1 text-[10px] font-mono uppercase tracking-widest ${diff > 0 ? "text-emerald-600" : diff < 0 ? "text-[#E63946]" : "text-black/40"}`}>
                      {diff > 0 ? "+" : ""}{diff} vs last
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top wishlists */}
          <div className="mb-10">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-3">Top wishlists this week</div>
            {stats.top_wishlists.length === 0 ? (
              <div className="text-sm text-black/50 font-serif italic">No wishlist activity in the last 7 days — share a link to get things moving.</div>
            ) : (
              <ul className="border border-black/10 bg-white" data-testid="top-wishlists">
                {stats.top_wishlists.map((w, i) => (
                  <li key={w.slug} className="px-4 py-3 border-b border-black/5 last:border-0 flex items-center justify-between gap-3" data-testid={`top-wishlist-${w.slug}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 flex-shrink-0 bg-[#111111] text-white font-serif font-black text-sm flex items-center justify-center">{i + 1}</div>
                      <div className="min-w-0 flex-1">
                        <Link to={`/w/${w.slug}`} className="font-serif text-lg font-bold hover:text-[#E63946] truncate block">{w.name}</Link>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-black/40">
                          {w.reactions_week} reactions &middot; {w.comments_week} comments &middot; {w.views_total} views total
                        </div>
                      </div>
                    </div>
                    <Link to={`/w/${w.slug}`} className="text-[10px] font-bold uppercase tracking-widest text-[#E63946] hover:underline flex-shrink-0">Open →</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Recent activity feed */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-3">Recent activity</div>
            {stats.recent_activity.length === 0 ? (
              <div className="text-sm text-black/50 font-serif italic" data-testid="activity-empty">The heat map&rsquo;s quiet — share a wishlist link to spark reactions.</div>
            ) : (
              <ul className="border border-black/10 bg-white" data-testid="activity-feed">
                {stats.recent_activity.map((a, i) => (
                  <li key={`${a.kind}-${a.wishlist_id || ""}-${a.deal_id || ""}-${a.created_at || i}`} className="px-4 py-3 border-b border-black/5 last:border-0 flex items-center gap-3 text-sm" data-testid={`activity-${i}`}>
                    <span className="w-8 flex-shrink-0 text-center text-xl leading-none">
                      {a.kind === "reaction" ? a.emoji : <MessageCircle className="w-4 h-4 inline text-[#E63946]" strokeWidth={2} />}
                    </span>
                    <div className="flex-1 min-w-0">
                      {a.kind === "reaction" ? (
                        <div className="font-serif">
                          Someone reacted to <Link to={`/w/${a.wishlist_slug}`} className="font-bold hover:text-[#E63946]">{a.wishlist_name}</Link>
                        </div>
                      ) : (
                        <div className="font-serif">
                          <span className="font-bold">{a.user_name}</span> on <Link to={`/w/${a.wishlist_slug}`} className="font-bold hover:text-[#E63946]">{a.wishlist_name}</Link>: <span className="text-black/60 italic">&ldquo;{a.text}&rdquo;</span>
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-black/30 flex-shrink-0">{fromNow(a.at)} ago</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
